import Axios from "./Axios";
import ENDPOINTS from "./endpoints";
import { unwrapData } from "./apiUtils";

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface LoginParams {
  identifier: string;
  password: string;
}

export interface LoginResponseData {
  accessToken: string;
  refreshToken?: string;
  tokenType: string;
  userId: number;
  name: string;
  email: string;
  provider: string;
  country?: string;
  role?: string;
}

export interface UserRole {
  roleId: number;
  roleName: string;
}

export interface MeResponse {
  userId: number;
  email: string;
  phone: string | null;
  name: string;
  username: string | null;
  statusId: number;
  roles: UserRole;
  enabled: boolean;
}

const login = async (params: LoginParams): Promise<LoginResponseData> => {
  const res = await Axios.post<ApiResponse<LoginResponseData>>(
    ENDPOINTS.auth.login,
    params
  );
  return res.data.data;
};

const getMe = async (): Promise<MeResponse> => {
  const res = await Axios.get<ApiResponse<MeResponse>>(ENDPOINTS.auth.me);
  return res.data.data;
};

export interface RefreshResponseData {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  userId?: number;
  name?: string;
  email?: string;
  provider?: string;
  country?: string;
  role?: string;
}

const refreshToken = async (refreshTokenValue: string): Promise<RefreshResponseData> => {
  const res = await Axios.post<any>(ENDPOINTS.auth.refresh, {
    refreshToken: refreshTokenValue,
  });
  return unwrapData(res);
};

export interface RegisterParams {
  name: string;
  firstName: string;
  lastName: string;
  emailId: string;
  mobile: string;
  password: string;
  country: string;
}

export interface OtpVerifyParams {
  identifier: string;
  otp: string;
}

export interface ForgotPasswordResetParams {
  identifier: string;
  otp: string;
  newPassword: string;
}

const register = async (params: RegisterParams): Promise<any> => {
  const res = await Axios.post<ApiResponse<any>>(ENDPOINTS.auth.register, {
    ...params,
    type: 1,
    provider: "LOCAL",
  });
  return res.data;
};

const verifyRegistrationOtp = async (params: OtpVerifyParams): Promise<any> => {
  const res = await Axios.post<ApiResponse<any>>(
    ENDPOINTS.auth.otpVerifyRegistration,
    { ...params, purpose: "registration" }
  );
  return res.data;
};

const sendOtp = async (identifier: string): Promise<any> => {
  const res = await Axios.post<ApiResponse<any>>(ENDPOINTS.otp.send, { identifier });
  return res.data;
};

const resendOtp = async (identifier: string): Promise<any> => {
  const res = await Axios.post<ApiResponse<any>>(ENDPOINTS.otp.resend, { identifier });
  return res.data;
};

const sendPasswordResetOtp = async (identifier: string): Promise<any> => {
  const res = await Axios.post<ApiResponse<any>>(ENDPOINTS.passwordReset.sendOtp, {
    identifier,
  });
  return res.data;
};

const resetPassword = async (params: ForgotPasswordResetParams): Promise<any> => {
  const res = await Axios.post<ApiResponse<any>>(ENDPOINTS.passwordReset.reset, params);
  return res.data;
};

const AuthService = {
  login,
  getMe,
  refreshToken,
  register,
  verifyRegistrationOtp,
  sendOtp,
  resendOtp,
  sendPasswordResetOtp,
  resetPassword,
};

export default AuthService;
